import { useEffect, useState } from 'react';

/**
 * useAiProvidersHealth — singleton client poller for the multi-provider navbar
 * chip (30 May 2026; re-homed to portal-shared 8 Jun 2026, ADR-055 follow-up).
 *
 * First mount starts a 10s interval, last unmount stops it, all subscribers
 * share the same cached array. The data SOURCE is injected (ADR-034 chip is
 * consumer-friendly and shown to staff AND subscribers — each portal supplies
 * its own fetchers):
 *   - admin/staff      → reads admin-api  /admin/diagnostics/ai-providers
 *   - subscriber       → reads portal-api /portal/diagnostics/ai-providers
 *
 * Both endpoints ultimately serve the SAME DynamoDB probe table
 * (Nexa-AiProviderHealth, written every 10 s by the ai-providers-probe Lambda —
 * ADR-041), so reads scale to tens of thousands of clients with zero provider
 * load. The hook itself is source-agnostic.
 *
 * History (ADR-041): on first mount the hook calls the source's `fetchHistory`
 * (if provided) to seed providerHistory with the last 60 minutes of server-side
 * data. Subsequent 10 s poll ticks add new HistoryPoint entries only when the
 * server's lastChecked timestamp advances (dedup guard). The graph is populated
 * immediately on open and survives Mac hibernation of any duration. A source
 * that omits `fetchHistory` simply starts the graph empty and fills it live.
 */

export type ProviderName   = 'anthropic' | 'openai' | 'xai';
export type ProviderStatus = 'loading' | 'online' | 'offline' | 'not-configured';

export interface ProviderHealth {
  provider:    ProviderName;
  status:      ProviderStatus;
  latencyMs:   number | null;
  lastChecked: string | null;
  errorCode?:  string;
  message?:    string;
}

/** A single latency sample in the per-provider history buffer. */
export interface HistoryPoint {
  /** Unix epoch milliseconds — when the probe resolved. */
  t: number;
  /** null when the provider was offline or not-configured that tick. */
  latencyMs: number | null;
}

interface ServerProvider {
  provider:    ProviderName;
  status:      'online' | 'offline' | 'not-configured';
  latencyMs:   number | null;
  lastChecked: string;
  errorCode?:  string;
  message?:    string;
}

export interface ServerResult {
  providers: ServerProvider[];
}

/** Shape returned by the latency-history endpoint. */
interface LatencyHistoryPoint {
  timestamp:  string;
  status:     'online' | 'offline' | 'not-configured';
  latencyMs:  number | null;
  errorCode?: string;
}

interface LatencyHistoryProvider {
  provider: ProviderName;
  points:   LatencyHistoryPoint[];
}

export interface LatencyHistoryResult {
  window:    string;
  providers: LatencyHistoryProvider[];
}

/**
 * Per-portal data source for the chip. Injected via NavBarContext (shared
 * navbar) or passed straight to the chip (portal-staff AppHeader). The poller
 * is a module singleton, so the source is captured once on first mount and is
 * expected to be stable for the lifetime of the portal bundle.
 */
export interface AiProvidersSource {
  /** Latest per-provider snapshot (the 10 s poll target). */
  fetchSnapshot: () => Promise<ServerResult>;
  /** Last 60 minutes of per-provider latency (seed on first mount). Optional —
   *  omit to start the graph empty and fill it from live polls. */
  fetchHistory?: () => Promise<LatencyHistoryResult>;
}

const POLL_INTERVAL_MS = 10_000;
const HISTORY_CAP      = 360;   // 1 hour at 10 s/poll
const PROVIDER_ORDER: ProviderName[] = ['anthropic', 'openai', 'xai'];

function initialCache(): ProviderHealth[] {
  return PROVIDER_ORDER.map((name) => ({
    provider:    name,
    status:      'loading' as ProviderStatus,
    latencyMs:   null,
    lastChecked: null,
  }));
}

function initialHistory(): Record<ProviderName, HistoryPoint[]> {
  return { anthropic: [], openai: [], xai: [] };
}

let cache:           ProviderHealth[]                     = initialCache();
let providerHistory: Record<ProviderName, HistoryPoint[]> = initialHistory();

// The injected source, captured on first mount (singleton — one source per
// portal bundle). Polling is a no-op until a source is registered.
let source: AiProvidersSource | null = null;

// Track the most recently recorded lastChecked per provider for dedup.
const lastRecordedTimestamp: Record<ProviderName, string | null> = {
  anthropic: null, openai: null, xai: null,
};

const subscribers        = new Set<(state: ProviderHealth[]) => void>();
const historySubscribers = new Set<(h: Record<ProviderName, HistoryPoint[]>) => void>();

let pollTimerId:     ReturnType<typeof setInterval> | null = null;
let activePoll:      Promise<void> | null = null;
let historySeeded    = false;

// ---------------------------------------------------------------------------
// History seeding — called once on first mount
// ---------------------------------------------------------------------------

async function seedHistoryFromServer(): Promise<void> {
  if (historySeeded || !source?.fetchHistory) {
    historySeeded = true;
    return;
  }
  try {
    const result = await source.fetchHistory();
    PROVIDER_ORDER.forEach((name) => {
      const serverProvider = result.providers.find((p) => p.provider === name);
      if (!serverProvider) return;
      const buf = providerHistory[name];
      serverProvider.points.forEach((pt) => {
        const t = Date.parse(pt.timestamp);
        if (isNaN(t)) return;
        buf.push({ t, latencyMs: pt.latencyMs });
      });
      // Respect cap (server should return ≤ 360 pts, but guard anyway)
      while (buf.length > HISTORY_CAP) buf.shift();
      // Record latest timestamp for dedup
      if (serverProvider.points.length > 0) {
        lastRecordedTimestamp[name] =
          serverProvider.points[serverProvider.points.length - 1]!.timestamp;
      }
    });
    historySeeded = true;
    notifyHistorySubscribers();
  } catch {
    // Non-fatal — graph starts empty if history endpoint unreachable.
    historySeeded = true;
  }
}

function notifyHistorySubscribers(): void {
  const snapshot: Record<ProviderName, HistoryPoint[]> = {
    anthropic: [...providerHistory.anthropic],
    openai:    [...providerHistory.openai],
    xai:       [...providerHistory.xai],
  };
  historySubscribers.forEach((fn) => fn(snapshot));
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------

async function poll(): Promise<void> {
  if (activePoll) return activePoll;
  if (!source) return;
  const activeSource = source;
  activePoll = (async () => {
    try {
      const result = await activeSource.fetchSnapshot();
      const byName = new Map(result.providers.map((p) => [p.provider, p]));
      cache = PROVIDER_ORDER.map((name) => {
        const next  = byName.get(name);
        if (next) return { ...next };
        const prior = cache.find((p) => p.provider === name);
        return prior ?? { provider: name, status: 'loading', latencyMs: null, lastChecked: null };
      });
    } catch (err) {
      const now = new Date().toISOString();
      cache = PROVIDER_ORDER.map((name) => ({
        provider:    name,
        status:      'offline' as ProviderStatus,
        latencyMs:   null,
        lastChecked: now,
        errorCode:   'client',
        message:     err instanceof Error ? err.message : 'Could not reach health endpoint.',
      }));
    } finally {
      // Push a new HistoryPoint only when lastChecked advances (dedup guard).
      // The server may return the same row for several client polls between
      // probe Lambda ticks — we don't want duplicate data points in the graph.
      const t = Date.now();
      cache.forEach((p) => {
        if (!p.lastChecked) return;
        if (lastRecordedTimestamp[p.provider] === p.lastChecked) return;
        lastRecordedTimestamp[p.provider] = p.lastChecked;
        const buf = providerHistory[p.provider];
        buf.push({ t, latencyMs: p.latencyMs });
        if (buf.length > HISTORY_CAP) buf.shift();
      });

      subscribers.forEach((fn) => fn(cache));
      notifyHistorySubscribers();
      activePoll = null;
    }
  })();
  return activePoll;
}

function startPolling(): void {
  if (pollTimerId !== null) return;
  void poll();
  pollTimerId = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
}

function stopPolling(): void {
  if (pollTimerId === null) return;
  clearInterval(pollTimerId);
  pollTimerId = null;
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
}

function onVisibilityChange(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    void poll();
  }
}

export function useAiProvidersHealth(dataSource: AiProvidersSource): {
  providers: ProviderHealth[];
  history:   Record<ProviderName, HistoryPoint[]>;
  refresh:   () => Promise<void>;
} {
  const [providers, setProviders] = useState<ProviderHealth[]>(cache);
  const [history,   setHistory]   = useState<Record<ProviderName, HistoryPoint[]>>(providerHistory);

  // Capture the source on first registration (singleton — one source per
  // portal bundle). A later, different source object is ignored: the chip is
  // mounted once per portal and the fetchers are stable.
  if (!source) source = dataSource;

  useEffect(() => {
    subscribers.add(setProviders);
    historySubscribers.add(setHistory);

    if (subscribers.size === 1) {
      // First mount: seed history from server, then start polling.
      void seedHistoryFromServer().then(() => startPolling());
    }

    return () => {
      subscribers.delete(setProviders);
      historySubscribers.delete(setHistory);
      if (subscribers.size === 0) stopPolling();
    };
  }, []);

  return { providers, history, refresh: poll };
}

export function __resetAiProvidersHealthCache(): void {
  cache           = initialCache();
  providerHistory = initialHistory();
  historySeeded   = false;
  source          = null;
  PROVIDER_ORDER.forEach((n) => { lastRecordedTimestamp[n] = null; });
  subscribers.clear();
  historySubscribers.clear();
  stopPolling();
}
